import qs from 'query-string';
import { isInIframe } from '@ui-kit/lib/helpers';
import { EDeployEnv } from 'shared/types';
import { routesScriptsure } from './routes.scriptsure';

/**
 * Env Variable Initialization Module
 * @remark If the value of at least one variable is not found,
 * the application will immediately throw an error during module initialization
 * @module
 */

/**
 * Getting the env variable
 * @throwable
 */
export const getEnvVar = (key: string): string => {
  if (process.env[key] === undefined) {
    throw new Error(`Env variable ${key} is required`);
  }
  return process.env[key] || '';
};

export const getOptionalEnvVar = (key: string): string => {
  return process.env[key] || '';
};

export const PUBLIC_URL = getEnvVar('PUBLIC_URL');

/** Program start mode */
export const NODE_ENV = getEnvVar('NODE_ENV');
/** Development mode */
export const isDevEnv = NODE_ENV === 'development' || NODE_ENV === 'test';
/** Production mode */
export const isProdEnv = NODE_ENV === 'production';

/** Deploy Environment for the application */
export const DEPLOY_ENV = getEnvVar('REACT_APP_DEPLOY_ENV');

export const DEPLOY_ENV_STAGE = DEPLOY_ENV === EDeployEnv.STAGE;
export const DEPLOY_ENV_PROD = DEPLOY_ENV === EDeployEnv.PROD;
export const RELEASE_TRACKER_ENABLED = DEPLOY_ENV === EDeployEnv.TEST || DEPLOY_ENV === EDeployEnv.DEV;

export const API_VERSION = getEnvVar('REACT_APP_API_VERSION');
export const APP_VERSION = getEnvVar('REACT_APP_APP_VERSION');

/** Can use mocks */
export const isDemoUseMock = getEnvVar('REACT_APP_DEMO_USE_MOCK') === 'true';

/** Add header "from-react: true" to all api requests */
export const USE_FROM_REACT_HEADER = getEnvVar('REACT_APP_USE_FROM_REACT_HEADER') === 'true';

/** API KEY */
export const API_KEY = getEnvVar('REACT_APP_API_KEY');

export const SESSION_TOKEN_QUERY_PARAM = 'sessiontoken';
export const SESSION_TOKEN_QUERY_PARAM_ALIAS = 'sessionToken';

const SEARCH_QUERY_PARAMS = qs.parse(window.location.search);

export const DARK_MODE_QUERY_PARAM = SEARCH_QUERY_PARAMS.darkmode?.toString() ?? null;

export const MOBILE_APP_QUERY_PARAM = SEARCH_QUERY_PARAMS.mobileApp?.toString() === 'true';

export const SESSION_TOKEN =
  SEARCH_QUERY_PARAMS[SESSION_TOKEN_QUERY_PARAM]?.toString() ?? SEARCH_QUERY_PARAMS[SESSION_TOKEN_QUERY_PARAM_ALIAS]?.toString() ?? null;

export const IN_IFRAME = isInIframe();

export const COOKIE_ENABLED = !IN_IFRAME && !SESSION_TOKEN;

export const LOCAL_STORAGE_ENABLED = !IN_IFRAME && !SESSION_TOKEN;

/** API URL */
export const API_URL_SCRIPTSURE = getEnvVar('REACT_APP_API_URL_SCRIPTSURE');
export const API_URL_PLATFORM = getEnvVar('REACT_APP_API_URL_PLATFORM');
export const API_URL_MEDICATION = getEnvVar('REACT_APP_API_URL_MEDICATION');

export const API_URL_SCRIPTSURE_V3 = `${API_URL_SCRIPTSURE}/v3`;
export const API_URL_PLATFORM_V3 = `${API_URL_PLATFORM}/v3`;

export const IDME_LOGIN_URL = getEnvVar('REACT_APP_URL_IDME');

/** APPLICATION URL */
export const URL_SCRIPTSURE = getEnvVar('REACT_APP_URL_SCRIPTSURE');
export const URL_PLATFORM = getEnvVar('REACT_APP_URL_PLATFORM');

export const DAWSYSTEMS_URL = 'https://www.dawsystems.com';

/** Help Menu URLs */
export const HELP_KNOWLEDGEBASE_URL = 'https://scriptsure.freshdesk.com/support/home';
export const HELP_SUBMIT_TICKET_URL = 'https://scriptsure.freshdesk.com/support/tickets/new';
export const HELP_WHATS_NEW_URL = '#';

/** Fresh Desk Support Links */
export const FD_SUPPORT_PROFILE_URL = 'https://scriptsure.freshdesk.com/support/solutions/articles/69000391515';
export const FD_SUPPORT_BAMBOO_URL = 'https://scriptsure.freshdesk.com/support/solutions/articles/69000852475';
export const FD_PROFILE_NO_DEA = '#User-Profile';
export const FD_PROFILE_NO_NEWRX = '#EPCS-Login';
export const FD_PROFILE_NO_CONTROLLEDRX = '#Checks-and-Alerts';
export const FD_PROFILE_NO_EPCS_APPROVAL = '#Patient-Adherence';

/** Stripe links */
export const STRIPE_CUSTOMER_URL = 'https://dashboard.stripe.com/customers/{{account_code}}';

export const COVER_MY_MEDS_URL = 'https://www.covermymeds.com/main/';

/** Breakpoints */
export const OBreakpoints = {
  '5xl': '(min-width: 1920px)',
  '4xl': '(min-width: 1680px)',
  '3xl': '(min-width: 1536px)',
  '2xl': '(min-width: 1440px)',
  'xl': '(min-width: 1280px)',
  'lg': '(min-width: 1024px)',
  'md': '(min-width: 768px)',
  'sm': '(min-width: 640px)',
  'xs': '(min-width: 425px)',
  '2xs': '(min-width: 390px)',
  '3xs': '(min-width: 320px)',
} as const;

export const DEMO_LINK = {
  CURRENT_PRACTICE: 'https://app.supademo.com/demo/cm7yvomgw005z090i5drma29g?embed_v=2',
  CURRENT_PROVIDER_P: 'https://app.supademo.com/demo/cm7xjiowx00cczn0ixu2iokdq?embed_v=2',
  CURRENT_PROVIDER_S: 'https://app.supademo.com/demo/cm7cc8nb807czw50iae5rezaj?embed_v=2',
  GRANT_ACCESS: 'https://app.supademo.com/demo/cm7cdp8g608a0xi0i8jo2cg94?embed_v=2',
  ADD_PATIENT: 'https://app.supademo.com/demo/cm7ccf6dn07yjxi0i9rn4tfwu?embed_v=2',
  FIND_PATIENT: 'https://app.supademo.com/demo/cm7ccai0v0747xs0itk0zblt8?embed_v=2',
  PATIENT_CHART: 'https://app.supademo.com/demo/cm7ccsy8907byxs0i3lep2enn?embed_v=2',
  ORDER_SETS: 'https://app.supademo.com/demo/cm8n4lti105dmwz0iju9qk5iw?embed_v=2',
  MANAGE_ORDER_SETS: 'https://app.supademo.com/demo/cm8j8sbzt0266wf0if6dc0yty?embed_v=2',
  ACCESS_REQUEST: 'https://app.supademo.com/demo/cm7xiqgvb0050zt0iomys482f?embed_v=2',
  ADD_MFAPLUS: 'https://app.supademo.com/demo/cm7wf8qqq00urxz0ijqbrhn9x?embed_v=2',
  ADD_USERS: 'https://app.supademo.com/demo/cm7yvdx250spjgvwcleeo4k1t?embed_v=2',
  EPA_FEATURE: 'https://app.supademo.com/demo/cmies9bzw00e5ya0igdrdc120?embed_v=2',
  MED_CART: 'https://app.supademo.com/demo/cmpvaicrr00190m0k5n6l3u33?utm_source=link',
  BYPASS_CONFIRM: 'https://app.supademo.com/demo/cmi6htxht01zi280hurvjoy13?embed_v=2',
  FRESHDESK: 'https://scriptsure.freshdesk.com/support/solutions/articles/69000863913-approve-controlled-substances-with-epcs-mfa-feature',
} as const;

/** Default Theme Colors & Grayscale */
export const DEFAULT_ALPHA_COLOR_THEME = getEnvVar('REACT_APP_DEFAULT_ALPHA_COLOR_THEME');
export const DEFAULT_BETA_COLOR_THEME = getEnvVar('REACT_APP_DEFAULT_BETA_COLOR_THEME');

export const APPLICATION = getEnvVar('REACT_APP_APPLICATION');

export const IS_SCRIPTSURE_APPLICATION = APPLICATION === 'scriptsure' || APPLICATION === 'scriptsure-vet';

export const IS_PLATFORM_APPLICATION = APPLICATION === 'platform' || APPLICATION === 'platform-vet';

export const IS_VET_APPLICATION = APPLICATION === 'scriptsure-vet' || APPLICATION === 'platform-vet';

/** Application Names */
export const APP_NAME_SCRIPTSURE = 'ScriptSure';
export const APP_NAME_VETWAY = 'VetWay';
export const APP_NAME = IS_VET_APPLICATION ? APP_NAME_VETWAY : APP_NAME_SCRIPTSURE;

export const RECAPTCHA_V2_SITE_KEY = getEnvVar('REACT_APP_RECAPTCHA_V2_SITE_KEY');

export const RECAPTCHA_DISABLED = getOptionalEnvVar('REACT_APP_RECAPTCHA_DISABLED') === 'true';

export const STRIPE_PK = getEnvVar('REACT_APP_STRIPE_PK');

export const LOGROCKET_APP_ID = getEnvVar('REACT_APP_LOGROCKET_ID');

export const DATADOG_RUM_ENABLED = getOptionalEnvVar('REACT_APP_DATADOG_RUM_ENABLED') === 'true';

export const DATADOG_RUM_APPLICATION_ID = getOptionalEnvVar('REACT_APP_DATADOG_RUM_APPLICATION_ID');

export const DATADOG_RUM_CLIENT_TOKEN = getOptionalEnvVar('REACT_APP_DATADOG_RUM_CLIENT_TOKEN');

export const DATADOG_RUM_VERSION = getOptionalEnvVar('REACT_APP_DATADOG_RUM_VERSION');

// for compatibility
export const routes = routesScriptsure;

export const APP_ID = 'appId';

export const CSS = 'css';

export const DARK_MODE = 'darkmode';

export const IS_AUTH_BY_SESSION_TOKEN = !!SESSION_TOKEN;

export const IS_WIDGET_APP = IS_AUTH_BY_SESSION_TOKEN && window.location.pathname.startsWith(routesScriptsure.widgets.path());

export const DIALOG_PRELOAD_ENABLED = getEnvVar('REACT_APP_DIALOG_PRELOAD_ENABLED') === 'true';

export const DIALOG_DEBUG = getOptionalEnvVar('REACT_APP_DIALOG_DEBUG') === 'true';

export const SOCKET_DEBUG = getOptionalEnvVar('REACT_APP_SOCKET_DEBUG') === 'true';

export const DHD_DEBUG = getOptionalEnvVar('REACT_APP_DHD_DEBUG') === 'true';

export const DOCEREE_DEBUG = getOptionalEnvVar('REACT_APP_DOCEREE_DEBUG') === 'true';

/* Status Center Vars - Atlassian Status Page */
export const STATUSPAGE_ENABLED = getEnvVar('REACT_APP_STATUSPAGE_ENABLED') === 'true';
export const STATUSPAGE_API_URL = getEnvVar('REACT_APP_STATUSPAGE_API_URL');
export const STATUSPAGE_BASE_URL = getEnvVar('REACT_APP_STATUSPAGE_BASE_URL');
export const STATUSPAGE_POLLING_INTERVAL = getEnvVar('REACT_APP_STATUSPAGE_POLLING_INTERVAL');
export const STATUSPAGE_BLOCKING_COMPONENTS = getEnvVar('REACT_APP_STATUSPAGE_BLOCKING_COMPONENTS');

export const HELP_TERMS_USE_URL = IS_VET_APPLICATION ? 'https://www.vetway.com/pdf/erxcloudterms.pdf' : 'https://www.dawsystems.com/pdf/erxcloudterms.pdf';
export const HELP_CODE_CONDUCT_URL = IS_VET_APPLICATION
  ? 'https://www.vetway.com/pdf/erxcloudconduct.pdf'
  : 'https://www.dawsystems.com/pdf/erxcloudconduct.pdf';
export const HELP_PRIVACY_POLICY_URL = IS_VET_APPLICATION
  ? 'https://www.vetway.com/pdf/erxcloudprivacy.pdf'
  : 'https://www.dawsystems.com/pdf/erxcloudprivacy.pdf';
